export const UI_EN = {
  header: {
    skipContent: "Skip to content",
    openMenu: "Open the menu",
    closeMenu: "Close the menu",
    menuLabel: "Navigation menu",
    book: "Book",
    bookStay: "Book your stay",
    homeLabel: "Le dolci Dimore — home",
  },
  intro: {
    caption: "Bed & Breakfast · Borgo Virgilio",
    skip: "Skip the introduction",
  },
  marquee: [
    "Le dolci Dimore",
    "Bed & Breakfast",
    "18th-century villa",
    "Borgo Virgilio",
    "Mantua",
    "Genuine hospitality",
  ],
  cta: {
    eyebrow: "Direct booking",
    defaultTitle: "Book directly, the simple way",
    defaultText:
      "Check up-to-date availability and rates on our official booking engine. Prefer to talk to us? We are available by phone or email.",
    book: "Check availability",
  },
  roomCard: { discover: "Discover the room", max: "max" },
  footer: {
    description:
      "Four rooms with private bathroom in an 18th-century villa surrounded by the countryside of Borgo Virgilio, about ten minutes from the centre of Mantua.",
    explore: "Explore",
    rooms: "The rooms",
    contacts: "Contact",
    maxGuests: "guests",
    booking: "Book your stay",
    rights: "Managed by",
    privacy: "Privacy policy",
    cookie: "Cookie policy",
  },
  mobileCta: { call: "Call", book: "Book now", whatsapp: "Message us on WhatsApp" },
  home: {
    seo: {
      title: "",
      description:
        "Bed and breakfast in Borgo Virgilio, 10 minutes from Mantua: four rooms with private bathroom in an 18th-century villa with a garden. Book directly.",
    },
    hero: {
      eyebrow: "Bed & Breakfast · Borgo Virgilio, Mantua",
      paragraph:
        "An 18th-century villa in the Mantuan countryside, ten minutes from the centre of Mantua. Four rooms with private bathroom, a garden and a personal welcome.",
      ctaRooms: "Discover the rooms",
      ctaBook: "Book your stay",
      alt: "The 18th-century villa of Le dolci Dimore seen from the garden",
    },
    intro: {
      eyebrow: "The villa",
      title: "A house with two and a half centuries of history, open to travellers",
      p1: "Le dolci Dimore is housed in an 18th-century villa in the countryside of Borgo Virgilio: four rooms, an indoor common area and a large garden. A small independent property, where hospitality is personal and your stay follows a slower rhythm — yours.",
      p2: "Its location is its quiet strength: surrounded by greenery, yet with Mantua's historic centre about ten minutes by car and the A22 Mantova Sud motorway exit just a few kilometres away.",
      link: "Discover the villa",
      alt: "Facade of the 18th-century villa of Le dolci Dimore",
    },
    video: {
      eyebrow: "The video",
      title: "The villa from above",
      text: "A one-minute flight over Le dolci Dimore: the villa's entrance, the rooms, the garden and the countryside of Borgo Virgilio all around.",
      play: "Watch the video of the villa",
      videoLabel: "Drone video of the Le dolci Dimore villa",
    },
    rooms: {
      eyebrow: "The rooms",
      title: "Four rooms, four ways to stay",
      text: "All on the first floor of the villa, all with private bathroom. What changes is the capacity: from two to four guests.",
      link: "Compare the rooms",
    },
    services: {
      eyebrow: "Services",
      title: "Everything essential, with the care of home",
      link: "All the details and practical information",
    },
    territory: {
      eyebrow: "The area",
      title: "Mantua, the Gonzaga, the Mincio: an area that deserves more than one night",
      text: "From the Gonzaga palace to the lotus flower bloom, from embankment cycle paths to UNESCO villages: we have collected the places worth discovering in a guide, with real distances from the property.",
      cta: "Explore the area guide",
      alt: "Mantua reflected on the waters of Lago Superiore",
    },
    audience: {
      tourism: {
        eyebrow: "For leisure travellers",
        title: "The right base to discover Mantua",
        text: "Ten minutes from the historic centre, a quarter of an hour from the Mincio Park, half an hour from Sabbioneta: from here you can explore the province without changing accommodation, and in the evening you return to the silence of the garden.",
        alt: "The outdoor seating in the garden of Le dolci Dimore",
      },
      business: {
        eyebrow: "For business travellers",
        title: "Convenient, quiet, no waiting",
        text: "A few kilometres from the A22 Mantova Sud exit, with assisted remote access even outside regular hours and self-organised breakfast in the room: designed for those who leave early and come back late.",
        alt: "The desk of Room 104 with TV and breakfast station",
      },
    },
    practical: { eyebrow: "Essential information" },
  },
  structure: {
    seo: {
      title: "The property: an 18th-century villa in Borgo Virgilio",
      description:
        "Le dolci Dimore is a B&B in an 18th-century villa in the countryside of Borgo Virgilio: four rooms, a common area and a garden, a few minutes from Mantua.",
    },
    eyebrow: "The property",
    h1: "An 18th-century villa, lived in with simplicity",
    lead: "Le dolci Dimore was born in an 18th-century villa in the countryside of Borgo Virgilio, along the Strada Romana that connects Mantua to its territory. The house preserves the proportions of the country residences of the time: thick walls, bright rooms, a garden that changes with the seasons.",
    s1: {
      title: "Four rooms, nothing more",
      text: "On the first floor of the villa are the four guest rooms: 101 and 104 Standard, 102 Superior and 103 Deluxe, all with private bathroom. Having few of them is a choice: it allows us to take care of every stay without the logic of big numbers.",
      link: "See the rooms",
      alt: "Main facade of the Le dolci Dimore villa",
    },
    s2: {
      title: "Shared spaces and a garden to slow down",
      p1: "Beyond the rooms, guests share an indoor common area and the large garden surrounding the villa. These are the spaces to read a few pages in the shade, have breakfast slowly or simply stay outdoors after a day around Mantua.",
      p2: "Our idea of hospitality is concrete: clean and tidy spaces, clear information before arrival, phone availability when needed. No impersonal front desk, no useless formalities.",
      alt: "The indoor common area of Le dolci Dimore, with the glass door open onto the garden",
    },
    s3: {
      title: "In the countryside, yet never far",
      text: "Borgo Virgilio borders Mantua: from the villa's gate to the historic centre it takes about ten minutes by car, and the A22 Mantova Sud exit is a few kilometres away. It is the right location for those who want to visit the city without sleeping in traffic, and for those who work in the area and look for a quiet place to come back to.",
      link: "Discover what to see nearby",
      alt: "The garden of Le dolci Dimore with its gravel paths",
    },
  },
  roomsPage: {
    seo: {
      title: "The rooms of the B&B near Mantua",
      description:
        "Four rooms with private bathroom in Borgo Virgilio: 101 and 104 Standard for 2 guests, 102 Superior for 3 and 103 Deluxe for 4. Check availability and book.",
    },
    eyebrow: "The rooms",
    h1: "Choose the right room for your stay",
    lead: "Four rooms on the first floor of the villa, all with private bathroom. What changes is the capacity and the bed configuration: from here you can tell which one suits you.",
    boxTitle: "What you find in every room",
    boxItems: [
      "Private bathroom reserved for the room",
      "Kettle and coffee machine",
      "Mini-fridge with water, juice and yogurt",
      "Breakfast products in the room",
      "First-floor setting in the villa",
      "Daily housekeeping by midday",
    ],
    ctaTitle: "Not sure which to choose? Let's talk",
    ctaText: "Tell us how you travel and how many you are: we will point you to the most suitable room. Or check availability right away on the booking engine.",
  },
  roomDetail: {
    seoTitle: (name, type, cap) => `${name} — ${type} for ${cap} guests`,
    seoDescription: (name, beds, cap, excerpt) =>
      `${name} at Le dolci Dimore, B&B near Mantua: ${beds}, private bathroom, up to ${cap} guests. ${excerpt}`,
    breadcrumb: "Rooms",
    theRoom: "The room",
    servicesTitle: "Room amenities",
    summary: "At a glance",
    maxCapacity: "Maximum capacity:",
    people: "people",
    privateBathroom: "Private bathroom in the room",
    recommendedFor: "Recommended for:",
    book: "Check availability and book",
    request: "Send a request",
    others: "The other rooms",
    upTo: "up to",
    guests: "guests",
    see: "View",
    detailAlt: (name) => `Detail of ${name}`,
  },
  servicesPage: {
    seo: {
      title: "Services and practical information",
      description:
        "Everything you need to know before booking Le dolci Dimore: private bathroom, breakfast in the room, garden, check-in 3:00–8:00 pm and check-out by 10:00 am.",
    },
    eyebrow: "Services and practical information",
    h1: "What you will find, explained without frills",
    lead: "Before booking, it is only fair to know exactly how your stay is organised. Here you will find services, times and access arrangements, exactly as they really are.",
    blocks: [
      {
        title: "Private bathroom in every room",
        text: "Each of the four rooms has its own private bathroom. No facilities are shared between guests: your room is a complete, reserved space.",
      },
      {
        title: "Breakfast, at your own pace",
        text: "No buffet and no timetable to respect: in your room you will find croissants, crispbread, cereals, biscuits, honey, jams and Nutella, with a kettle and a coffee machine. In the fridge there are water, juice and yogurt. You organise yourself as you prefer, even if you leave early for work.",
      },
      {
        title: "The garden",
        text: "The large garden surrounding the villa is available to guests: the right place for an outdoor break, a book, a coffee in the shade.",
      },
      {
        title: "The indoor common area",
        text: "Inside the villa there is a shared space available to guests, with a glass door that opens directly onto the garden: designed for a moment of relaxation outside your room.",
      },
      {
        title: "The rooms on the first floor",
        text: "The rooms are on the first floor of the villa, reached by stairs. For specific mobility needs, please contact us before booking, so we can assess the most suitable accommodation together.",
      },
      {
        title: "Access to the property",
        text: "Whenever possible, check-in is in person: we welcome you ourselves. Otherwise we arrange remote access, with clear instructions before arrival and phone assistance while you enter. In both cases, you will never be left without a point of contact.",
      },
    ],
    hours: [
      {
        title: "Check-in and check-out",
        textPre: "Arrival is from ",
        textStrong1: "3:00 pm to 8:00 pm",
        textMid: " and the room must be vacated ",
        textStrong2: "by 10:00 am",
        textPost: ". For different times, contact us in advance: we will do our best to accommodate you.",
      },
      {
        title: "Housekeeping",
        text: "Rooms are refreshed according to the property's organisation, with cleaning completed by 12:00 pm.",
      },
      {
        title: "A note of transparency",
        text: "There is no 24-hour reception: we are a small, directly managed property. We remain reachable by phone for any need during your stay.",
      },
    ],
  },
  territory: {
    seo: {
      title: "The area: what to see in Mantua and its surroundings",
      description:
        "A guide to the area for guests of Le dolci Dimore: Mantua and the Gonzaga, Palazzo Ducale and Palazzo Te, the Mincio Park, the villages and Mantuan cuisine.",
    },
    eyebrow: "The area",
    h1: "Mantua and its land, told by those who live here",
    lead: "Le dolci Dimore is in a strategic position: Mantua's historic centre is about ten minutes by car, and from here the roads lead to the Mincio, the villages of the province and the best tables of the tradition. The distances shown are driving times, to be checked at the time of travel.",
    navLabel: "Area categories",
    credits:
      "Photo credits for the area: Carlo Pelagalli (Mantua skyline and lotus flowers, CC BY-SA 3.0), Carlo Alberto Magagnini (Piazza Sordello and Palazzo Ducale, CC BY-SA 4.0), Michael S-R (tortelli di zucca, CC BY-SA 2.0), via Wikimedia Commons.",
    ctaTitle: "A convenient base to explore all of this",
    ctaText: "Sleeping at Le dolci Dimore you reach Mantua in ten minutes and keep the rest of the area within a day's reach. Check room availability.",
  },
  guide: {
    seo: {
      title: "The guide: what to see and do in Mantua",
      description:
        "Articles and itineraries to plan your stay in Mantua: what to see in one day, a weekend among the Gonzaga, the Mincio Park and Mantuan cuisine.",
    },
    eyebrow: "The guide",
    h1: "Itineraries and tips for your stay in Mantua",
    lead: "Carefully written articles on what to see, where to walk and what to eat in Mantua and its surroundings. We update them over time, with the same information we give our guests.",
    read: "Read the article",
    minRead: "read",
    credits:
      "Article photo credits: Carlo Pelagalli and Carlo Alberto Magagnini (Mantua and the Mincio, CC BY-SA 3.0/4.0), Michael S-R (tortelli di zucca, CC BY-SA 2.0), via Wikimedia Commons.",
  },
  article: {
    back: "The guide",
    minRead: "read",
    continueReading: "Keep reading",
    read: "Read",
    ctaTitle: "Turn the itinerary into a stay",
    ctaText: "Le dolci Dimore is the ideal base to follow these itineraries: rooms with private bathroom ten minutes from Mantua, in the quiet of the countryside.",
  },
  faq: {
    seo: {
      title: "Frequently asked questions",
      description:
        "Answers to the most frequent questions about Le dolci Dimore: location, rooms, check-in and check-out times, breakfast, remote access and bookings.",
    },
    eyebrow: "Frequently asked questions",
    h1: "Everything you want to know before booking",
    introPre: "If you can't find the answer you are looking for, call us at ",
    introPost: " or write to us: we are happy to reply.",
  },
  contact: {
    seo: {
      title: "Contact and how to get here",
      description:
        "Contact Le dolci Dimore: Strada Romana 69/A, Borgo Virgilio (MN), Italy. Phone +39 0376 1587002, email info@ledolcidimore.it. Enquiry form and directions.",
    },
    eyebrow: "Contact",
    h1: "Let's talk: we are here to help you plan your stay",
    labels: {
      address: "Address",
      phone: "Phone",
      email: "Email",
      whatsapp: "WhatsApp",
      whatsappNote: "Quick replies for enquiries and arrivals.",
      hours: "Times",
      hoursText: "Check-in from 3:00 pm to 8:00 pm · Check-out by 10:00 am. Arriving outside these hours? Contact us in advance and we will arrange remote access.",
      arrival: "How to get here",
      arrivalText: "By car: A22 Mantova Sud motorway exit, a few kilometres from the property. Mantua's historic centre is about ten minutes away. For parking information, contact us before arrival.",
      directions: "Open directions",
    },
    map: {
      title: "Map: Le dolci Dimore, Strada Romana 69/A, Borgo Virgilio",
      load: "Load the interactive map",
      consent: "By clicking you load content from Google Maps, subject to its privacy policy.",
    },
    form: {
      title: "Enquiry form",
      intro: "Write to us for information, special requests or to organise your arrival. For availability and immediate booking, use the ",
      introLink: "official booking engine",
      name: "Full name *",
      email: "Email *",
      phone: "Phone",
      arrival: "Arrival",
      departure: "Departure",
      message: "Message *",
      company: "Company",
      privacyPre: "I have read the ",
      privacyLink: "privacy policy",
      privacyPost: " and I consent to the processing of my data to answer my enquiry. *",
      submit: "Send the enquiry",
      sending: "Sending…",
      errName: "Please enter your name",
      errEmail: "Please enter a valid email address",
      errMessage: "Please write a message of at least 10 characters",
      errPrivacy: "You must accept the privacy policy",
      toastOk: "Enquiry sent. We will get back to you as soon as possible.",
      toastErr: "We could not send your enquiry. Please try again or call us at ",
      successTitle: "Enquiry sent",
      successTextPre: "Thank you for writing to us. We will reply as soon as possible to the email address you provided. For urgent matters you can call us at ",
      newRequest: "Send another enquiry",
    },
  },
  privacy: {
    seo: {
      title: "Privacy policy",
      description: "Information on the processing of personal data by Le dolci Dimore, managed by Carmadue9 Srl.",
    },
    eyebrow: "Document being updated with the privacy consultant",
    h1: "Privacy policy",
    updated: "Last updated: July 2026",
    sections: [
      {
        title: "Data controller",
        text: "The data controller is Carmadue9 Srl, operator of the Le dolci Dimore property, Strada Romana 69/A, 46034 Borgo Virgilio (MN), Italy. For any information you can write to info@ledolcidimore.it or call +39 0376 1587002.",
      },
      {
        title: "Data collected through the contact form",
        text: "When you fill in the enquiry form we collect your name, email address, phone number (optional), any stay dates and the content of your message. This data is used exclusively to answer your enquiry and is not shared with third parties nor used for marketing purposes without your consent.",
      },
      {
        title: "Bookings via Beddy",
        text: "Bookings are made on the external Beddy booking engine (ledolcidimore.beddy.io). Data entered during booking is processed by Beddy according to its own privacy policy, which we invite you to read on the provider's website.",
      },
      {
        title: "Browsing data and analytics tools",
        text: "The site may collect technical browsing data necessary for operation and security. Any future integration of statistical analysis tools will comply with regulations and will acquire consent where required.",
      },
      {
        title: "Your rights",
        text: "Under Regulation (EU) 2016/679 (GDPR) you may request access, rectification, erasure or restriction of the processing of your personal data at any time, by writing to info@ledolcidimore.it.",
      },
    ],
  },
  cookie: {
    seo: {
      title: "Cookie policy",
      description: "Information on the cookies used by the Le dolci Dimore website.",
    },
    eyebrow: "Document being updated with the privacy consultant",
    h1: "Cookie policy",
    updated: "Last updated: July 2026",
    sections: [
      {
        title: "What cookies are",
        text: "Cookies are small text files that visited sites send to the user's device, where they are stored to be sent back to the same sites on the next visit.",
      },
      {
        title: "Cookies used by this site",
        text: "This site uses technical cookies, necessary for the pages to function and for browsing preferences, which do not require user consent. The Google Maps map on the Contact page is loaded only after a voluntary action (clicking the map panel): until that moment, no third-party cookies related to the map are installed.",
      },
      {
        title: "Third-party cookies",
        text: "Any future activation of analytics tools or third-party content will take place after consent is acquired through the appropriate panel, with the option to change preferences at any time.",
      },
      {
        title: "How to manage cookies from your browser",
        text: "You can manage or delete cookies directly from your browser settings. Disabling technical cookies may compromise the proper functioning of some parts of the site.",
      },
      {
        title: "Contact",
        text: "For questions about this cookie policy you can write to info@ledolcidimore.it.",
      },
    ],
  },
  notFound: {
    seo: { title: "Page not found", description: "The page you are looking for does not exist or has been moved." },
    eyebrow: "Error 404",
    h1: "This page doesn't exist, but the countryside does",
    text: "The content you were looking for may have been moved. Go back to the homepage or discover the rooms of Le dolci Dimore.",
    home: "Back to home",
    rooms: "See the rooms",
  },
};
